"use client";
import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/services/Firebase/Authcontext";
import { checkUserOnboardingStatus } from "@/services/APIs/OnboardingService";

interface OnboardingGuardProps {
  children: React.ReactNode;
  skipOnPaths?: string[]; // Paths where onboarding check should be skipped
}

const DEFAULT_SKIP_PATHS = ["/onboarding", "/account", "/logout", "/login", "/signup"];

export default function OnboardingGuard({
  children,
  skipOnPaths = DEFAULT_SKIP_PATHS,
}: OnboardingGuardProps) {
  const { sessionUser: user, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    const checkOnboarding = async () => {
      if (loading || !user?.uid) return;

      // Skip onboarding check for certain paths
      const currentPath = window.location.pathname;
      const shouldSkip = skipOnPaths.some((path) =>
        currentPath.startsWith(path)
      );
      if (shouldSkip) return;

      try {
        const onboardingStatus = await checkUserOnboardingStatus(user.uid);

        // Redirect new users to onboarding
        if (onboardingStatus.isNewUser) {
          router.push("/onboarding/welcome");
          return;
        }

        // Redirect users who need account setup
        if (onboardingStatus.needsAccountSetup) {
          router.push("/account");
          return;
        }

        // For users with incomplete profiles, show notification but allow dashboard access
        // This is less aggressive than middleware - users can still navigate
        if (
          onboardingStatus.needsProfileCompletion &&
          currentPath === "/dashboard"
        ) {
          // You could show a banner notification here instead of redirecting
          console.log(
            "Profile completion recommended:",
            onboardingStatus.recommendations
          );
        }
      } catch (error) {
        console.error("Error checking onboarding status:", error);
      }
    };

    checkOnboarding();
  }, [user, loading, router, skipOnPaths]);

  return <>{children}</>;
}
